// HiPPO Email Capture - CoStar-style "Almost there" screen
// Appears AFTER diagnostic questions with smooth transition, BEFORE the analysing animation

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, ArrowRight, Lock, User, Shield, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "@/components/ThemeProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero image for background
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_61f9b4e5-9795-47b1-942b-e3ab1711fd8f/artifacts/jiw8ixlc_edensdad._cinematic_close-up_cross-view_of_luxury_British_home__aa4984b8-4df0-4c41-b9bc-f7261784d14d.png";

const ANALYSING_TEXTS = [
  "Processing property signals...",
  "Analysing spatial patterns...",
  "Calculating potential...",
  "Generating insight..."
];

// Scanning Reticle for analysing screen
function ScanningReticle({ className = "" }) {
  return (
    <div className={`relative w-24 h-24 ${className}`}>
      <div className="absolute inset-0 rounded-full border-2 border-cipher-green/20">
        <div className="absolute inset-0 rounded-full border-t-2 border-cipher-green/60 animate-spin" style={{ animationDuration: '2s' }} />
      </div>
      <div className="absolute inset-3 rounded-full border border-cipher-green/30 animate-pulse" />
      <div className="absolute inset-6 rounded-full bg-cipher-green/10" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-cipher-green animate-pulse" />
      </div>
    </div>
  );
}

// Pulse Rings Animation for analysing screen
function PulseRingsAnimation() {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const dpr = window.devicePixelRatio || 1;
    let canvasWidth = 0;
    let canvasHeight = 0;
    
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvasWidth = rect.width || window.innerWidth;
      canvasHeight = rect.height || window.innerHeight;
      
      if (canvasWidth > 0 && canvasHeight > 0) {
        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
      }
    };
    resize();
    
    const rings = [];
    const maxRings = 8;
    let lastRingTime = 0;
    const ringInterval = 800;
    
    let animationFrame;
    let startTime = Date.now();
    
    const draw = () => {
      if (canvasWidth <= 0 || canvasHeight <= 0) {
        resize();
        animationFrame = requestAnimationFrame(draw);
        return;
      }
      
      const now = Date.now();
      
      // Clear with dark background and slight trail
      ctx.fillStyle = 'rgba(30, 30, 30, 0.15)';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // Add new ring periodically
      if (now - lastRingTime > ringInterval && rings.length < maxRings) {
        rings.push({
          radius: 0,
          opacity: 0.4,
          birth: now
        });
        lastRingTime = now;
      }
      
      const centerX = canvasWidth / 2;
      const centerY = canvasHeight / 2;
      const maxRadius = Math.max(canvasWidth, canvasHeight) * 0.6;
      
      // Draw and update rings
      rings.forEach((ring) => {
        const age = now - ring.birth;
        const progress = age / 4000;
        
        ring.radius = progress * maxRadius;
        ring.opacity = 0.3 * (1 - progress);
        
        if (ring.opacity > 0.01) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, ring.radius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(0, 106, 77, ${ring.opacity})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      
      // Remove dead rings
      for (let i = rings.length - 1; i >= 0; i--) {
        if (rings[i].opacity <= 0.01) {
          rings.splice(i, 1);
        }
      }
      
      // Draw floating particles
      const elapsed = now - startTime;
      const particleCount = 20;
      for (let i = 0; i < particleCount; i++) {
        const angle = (i / particleCount) * Math.PI * 2 + elapsed * 0.0003;
        const distance = 100 + Math.sin(elapsed * 0.001 + i) * 50;
        const x = centerX + Math.cos(angle) * distance;
        const y = centerY + Math.sin(angle) * distance;
        const size = 2 + Math.sin(elapsed * 0.002 + i * 0.5) * 1;
        
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 106, 77, ${0.15 + Math.sin(elapsed * 0.002 + i) * 0.1})`;
        ctx.fill();
      }
      
      animationFrame = requestAnimationFrame(draw);
    };
    
    draw();
    window.addEventListener('resize', resize);
    
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
    };
  }, []);
  
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
}

export default function HiPPOEmailCapture() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [analysingTextIndex, setAnalysingTextIndex] = useState(0);
  const [entranceComplete, setEntranceComplete] = useState(false);

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setEntranceComplete(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (location.state?.diagnosticData) {
      setDiagnosticData(location.state.diagnosticData);
      return;
    }
    
    const stored = sessionStorage.getItem(`hippo_diagnostic_${sessionId}`);
    if (stored) {
      setDiagnosticData(JSON.parse(stored));
    } else {
      toast.error("Session expired. Please start again.");
      navigate("/");
    }
  }, [sessionId, location.state, navigate]);

  // Analysing text animation
  useEffect(() => {
    if (analysing) {
      const interval = setInterval(() => {
        setAnalysingTextIndex(prev => prev < ANALYSING_TEXTS.length - 1 ? prev + 1 : prev);
      }, 700);
      return () => clearInterval(interval);
    }
  }, [analysing]);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {
    if (!firstName.trim()) {
      toast.error("Please enter your first name");
      return;
    }
    
    if (!email.trim() || !isValidEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!diagnosticData) {
      toast.error("Diagnostic data not found. Please try again.");
      return;
    }

    setSubmitting(true);
    setAnalysing(true);
    setAnalysingTextIndex(0);
    const submitStartTime = Date.now();
    
    try {
      const payload = {
        first_name: firstName.trim(),
        postcode: diagnosticData.postcode,
        property_era: diagnosticData.property_era || "classic",
        dwelling_type: diagnosticData.dwelling_type,
        bedrooms: diagnosticData.bedrooms,
        bathrooms: parseInt(diagnosticData.bathrooms) || 1,
        separate_kitchen: diagnosticData.separate_kitchen,
        wide_hallways_over_1m: diagnosticData.wide_hallways_over_1m,
        layout_changed_since_built: diagnosticData.layout_changed_since_built,
        features: diagnosticData.features || [],
        epc_band: diagnosticData.epc_band,
        main_goal: diagnosticData.main_goal,
        email: email.trim().toLowerCase(),
        consent: true
      };

      const response = await axios.post(`${API}/hippo/teaser`, payload);
      
      const submissionId = response.headers["x-submission-id"];
      const accessToken = response.headers["x-access-token"];
      
      if (!submissionId) {
        throw new Error("No submission ID returned");
      }
      
      // Store all the data for the insight page (including features for PropertySchematic)
      sessionStorage.setItem(`hippo_data_${submissionId}`, JSON.stringify(response.data));
      sessionStorage.setItem(`hippo_token_${submissionId}`, accessToken);
      sessionStorage.setItem(`hippo_session_${submissionId}`, JSON.stringify({
        first_name: firstName.trim(),
        postcode: diagnosticData.postcode,
        email: email.trim().toLowerCase(),
        dwelling_type: diagnosticData.dwelling_type,
        bedrooms: diagnosticData.bedrooms,
        property_era: diagnosticData.property_era,
        features: diagnosticData.features || [],
        separate_kitchen: diagnosticData.separate_kitchen,
        main_goal: diagnosticData.main_goal
      }));
      
      // Ensure analysing screen shows for at least 2s total from submit click
      const elapsed = Date.now() - submitStartTime;
      const remainingDelay = Math.max(0, 2000 - elapsed);
      setTimeout(() => {
        navigate(`/hippo/${submissionId}`);
      }, remainingDelay);
      
    } catch (error) {
      console.error("HiPPO submission error:", error);
      toast.error("Something went wrong. Please try again.");
      setAnalysing(false);
      setSubmitting(false);
    }
  };

  // Analysing Screen - Frosted Glass with pulse rings
  if (analysing) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden">
        {/* Hero image background with frosted glass effect */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt=""
            className="w-full h-full object-cover"
          />
          {/* Frosted glass overlay */}
          <div className="absolute inset-0 backdrop-blur-md bg-black/50" />
        </div>
        
        <PulseRingsAnimation />
        
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
          {/* Glassmorphism card for analysing content */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl max-w-sm w-full">
            <div className="flex justify-center mb-6">
              <ScanningReticle />
            </div>
            
            <div className="text-center">
              <p className="text-lg font-medium text-white mb-2 animate-pulse">
                {ANALYSING_TEXTS[analysingTextIndex]}
              </p>
              {firstName && (
                <p className="text-sm text-white/50">Preparing {firstName}'s insight</p>
              )}
            </div>
            
            <div className="flex items-center justify-center gap-2 mt-6">
              {ANALYSING_TEXTS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-500 ${
                    i <= analysingTextIndex ? "bg-cipher-green" : "bg-white/20"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        
        <footer className="relative z-10 p-4 text-center">
          <p className="text-xs text-white/30">HiPPO by StudioNPG</p>
        </footer>
      </div>
    );
  }

  if (loading || !diagnosticData) {
    return (
      <div className="fixed inset-0 flex flex-col overflow-hidden">
        {/* Background with frosted glass - same as main screen for consistency */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 backdrop-blur-md bg-black/60" />
        </div>
        <div className="relative z-10 flex-1 flex items-center justify-center">
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
            <div className="w-10 h-10 border-2 border-cipher-green/30 border-t-cipher-green rounded-full animate-spin mx-auto" />
            <p className="text-sm text-white/60 mt-4">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 flex flex-col overflow-hidden transition-opacity duration-500 ${entranceComplete ? 'opacity-100' : 'opacity-0'}`}>
      {/* Background with frosted glass */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 backdrop-blur-md bg-black/60" />
      </div>
      
      {/* Main Content */}
      <main className="relative z-10 flex-1 flex flex-col justify-center p-4">
        <div className="max-w-sm mx-auto w-full">
          {/* Glassmorphism Card */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6 shadow-2xl animate-fade-up">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-2xl bg-cipher-green/20 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-cipher-green" />
              </div>
            </div>
            
            {/* Heading */}
            <div className="text-center mb-6">
              <h1 className="font-bold text-2xl text-white mb-2">
                One last step
              </h1>
              <p className="text-white/60 text-sm">
                Where should we send your insight?
              </p>
            </div>

            {/* Form Fields */}
            <div className="space-y-4 mb-6">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type="text"
                  placeholder="Your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="h-14 bg-white/10 border-white/20 text-white placeholder:text-white/40 pl-11 rounded-xl focus:border-cipher-gold focus:ring-cipher-gold/30"
                  data-testid="hippo-name-input"
                />
              </div>
              
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-14 bg-white/10 border-white/20 text-white placeholder:text-white/40 pl-11 rounded-xl focus:border-cipher-gold focus:ring-cipher-gold/30"
                  autoFocus
                  data-testid="hippo-email-input"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              className="w-full bg-cipher-green hover:bg-cipher-green/90 text-white py-6 rounded-xl font-medium shadow-lg shadow-cipher-green/30 hover:shadow-xl hover:scale-[1.01] transition-all"
              disabled={submitting || !firstName.trim() || !email.trim()}
              data-testid="hippo-reveal-insight-btn"
            >
              {submitting ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Reveal My Insight
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            {/* Inline consent + security */}
            <p className="text-[11px] text-white/40 text-center mt-3 leading-relaxed">
              By continuing, you agree to our{" "}
              <a href="#" className="text-cipher-gold/70 underline">Privacy Policy</a>
              {" "}and{" "}
              <a href="#" className="text-cipher-gold/70 underline">Terms</a>.
              Your data is protected and never shared.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 p-4">
        <div className="flex items-center justify-center gap-2 text-xs text-white/30">
          <Shield className="w-3 h-3" />
          <span>HiPPO by StudioNPG</span>
        </div>
      </footer>
    </div>
  );
}
